import { useEffect, useRef, useState } from 'react';
import { StorageKeys, readStorage, writeStorage } from '../utils/storage';

/**
 * Auto-refresh scheduler.
 *
 * Design constraints, in order of importance:
 *
 * 1. **Never poll a hidden tab.** The Page Visibility API pauses the timer when
 *    the dashboard is in a background tab or the screen is locked. A wall
 *    display left open overnight must not hammer a shared read-only database
 *    for twelve hours.
 * 2. **Refresh on return.** When the tab becomes visible again, whatever is on
 *    screen is stale by definition, so refresh once immediately rather than
 *    waiting out the remainder of the interval.
 * 3. **Never overlap.** A tick is skipped while a previous refresh is still in
 *    flight, so a slow query cannot pile requests on top of each other.
 *
 * Status changes matter as much as new orders here: an order placed weeks ago
 * can be picked up today, so polling is useful for historical ranges too — the
 * counts move even when the order date does not.
 */
export const REFRESH_INTERVALS = [
  { value: 0, label: 'Off' },
  { value: 30_000, label: '30 sec' },
  { value: 60_000, label: '1 min' },
  { value: 300_000, label: '5 min' },
] as const;

export const DEFAULT_INTERVAL_MS = 60_000;

export function loadStoredInterval(): number {
  const raw = readStorage(StorageKeys.refreshInterval);
  const parsed = raw === null ? NaN : Number(raw);
  return REFRESH_INTERVALS.some((i) => i.value === parsed) ? parsed : DEFAULT_INTERVAL_MS;
}

export interface AutoRefreshOptions {
  /** Milliseconds between refreshes. `0` disables polling entirely. */
  intervalMs: number;
  /** Fired when a refresh should happen. */
  onRefresh: () => void;
  /** True while a refresh is still running; ticks are skipped until it clears. */
  busy: boolean;
}

export function useAutoRefresh({ intervalMs, onRefresh, busy }: AutoRefreshOptions): {
  /** False while the tab is hidden, so the UI can say polling is paused. */
  active: boolean;
} {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');

  // Keep the latest callback/flag without restarting the interval each render.
  const refreshRef = useRef(onRefresh);
  const busyRef = useRef(busy);
  refreshRef.current = onRefresh;
  busyRef.current = busy;

  useEffect(() => {
    const onVisibility = () => {
      const nowVisible = document.visibilityState === 'visible';
      setVisible(nowVisible);
      // Returning to a stale tab: refresh straight away.
      if (nowVisible && intervalMs > 0 && !busyRef.current) refreshRef.current();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [intervalMs]);

  useEffect(() => {
    if (intervalMs <= 0 || !visible) return;

    const id = window.setInterval(() => {
      // Skip rather than queue when a refresh is already running.
      if (!busyRef.current) refreshRef.current();
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [intervalMs, visible]);

  useEffect(() => {
    writeStorage(StorageKeys.refreshInterval, String(intervalMs));
  }, [intervalMs]);

  return { active: intervalMs > 0 && visible };
}
